"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getCSSTransition = getCSSTransition;
exports.getEase = getEase;
exports.getTransitionSettings = getTransitionSettings;
exports.setDuration = setDuration;
/* eslint-disable no-use-before-define */

const motionSettings = {
  ease: {
    swift: [0.16, 1, 0.3, 1],
    fluid: [0.1, 0.25, 0.3, 1],
    rebound: [0.34, 1.36, 0.64, 1]
  },
  baseVariants: {
    pre: {
      scale: 0.985,
      opacity: 0
    },
    active: {
      y: '0%',
      x: '0%',
      scale: 1,
      opacity: [0, 1, 1]
    }
  },
  staggerBase: 0.035,
  staggerMultiplier: 0.05,
  opacityKeyframes: [0, 1, 1],
  durationMultiplier: 0.1,
  durationBase: {
    swift: 0.3,
    fluid: 0.6,
    rebound: 0.6
  }
};

/**
 * getCSSTransition
 * Use to import transition property values into Linaria or JSS stylesheets
 *
 * @example
 *
 * ```jsx
 * // in a styled component
 * const SomeComponent = styled.div`
 *    // ...
 *    transition: ${ getCSSTransition('swift', '100', ['transform', 'opacity']) }
 * `
 * ```
 *
 * ```jsx
 * // in a css const
 * const someCSS = css`
 *    .some-class {
 *        // ...
 *        transition: ${ getCSSTransition('swift', '100', ['transform', 'opacity']) }
 *    }
 * ```
 *
 * ```jsx
 * // with inline styles
 * <div
 *    style={{
 *         transition: getCSSTransition('swift', '100', ['transform', 'opacity'])
 *    }}
 * >
 *    <p>I'm a div!</p>
 * </div>
 * ```
 *
 *
 * @param {string} mode either "swift", "fluid", or "rebound"
 * @param {number} intensity decides how far and how quick, step of 100 between 100 and 500
 * @param {string|string[]} properties an array of string of css properties to animate
 * @returns {string} css transition property value
 */
function getCSSTransition(mode = 'swift', intensity = 100, properties = ['transform', 'opacity']) {
  const transition = getTransitionSettings(mode, intensity);
  const easeArray = transition.ease;
  const ease = `cubic-bezier(${easeArray.join(', ')})`;
  const durationNumber = transition.duration;
  const duration = `${durationNumber * 1000}ms`;
  if (Array.isArray(properties)) {
    properties = properties.map(item => `${item} ${duration} ${ease}`);
    properties = properties.join(', ');
  } else {
    properties = `${properties} ${duration} ${ease}`;
  }
  return properties;
}

/**
 * setDuration
 * A local function to calculate duration based on a base value,
 * and a multiplier, used in setting base durations for modes
 *
 * @param {Number} base base duration
 * @param {Number} multiplier multiplier fraction
 * @param {Number} intensity level of multiplication
 * @returns {Number} duration value
 */
function setDuration(base, multiplier, intensity) {
  const level = parseInt(intensity, 10) / 100 - 1;
  return Number.parseFloat(base + multiplier * level).toFixed(3);
}

/**
 * Get the transition props for given settings
 *
 * @param {string} mode - Transition type "swift", "fluid", or "rebound"
 * @param {number} intensity - Choose 100, 200, 300, 400, or 500
 * @param {string} variantSet - Variants settings name
 * @returns {object} transition
 */
function getTransitionSettings(mode = 'swift', intensity = 100, variantSet) {
  const hasStagger = variantSet && /staggerChildren/i.test(variantSet);
  const transition = {
    duration: setDuration(motionSettings.durationBase[mode], motionSettings.durationMultiplier, intensity),
    ease: motionSettings.ease[mode]
  };
  if (hasStagger) {
    transition.staggerChildren = setDuration(motionSettings.staggerBase, motionSettings.staggerMultiplier, intensity);
  }
  return transition;
}

/**
 * getEase
 * Returns the cubic-bezier control points array for a given mode
 * @param {('swift'|'fluid'|'rebound')} mode easing mode
 * @returns {number[]} cubic-bezier array
 */
function getEase(mode = 'fluid') {
  return motionSettings.ease[mode] || motionSettings.ease.fluid;
}